import React, { useState, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Users, Pencil, Check, X, Trash2, ChevronDown, ChevronRight, Save, Search, Download, Upload, ShieldCheck, ShieldAlert, Wand2, Loader2 } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { useGenerateCampaignEmails } from '@/hooks/useCampaignAI';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { stripHtml } from '@/utils/htmlUtils';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { Campaign } from '@/services/campaignService';
import { importRecipientsFromCSV } from '@/services/campaignRecipientImportService';
import { toast } from 'sonner';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: Campaign;
}

interface QueueItem {
  id: string;
  recipient_name: string | null;
  recipient_email: string;
  subject: string;
  status: string;
  sent_at: string | null;
  failed_at: string | null;
  html_content: string | null;
  text_content: string | null;
  verification_status: string | null;
  verification_notes: string | null;
  lead_id: string | null;
  leads: { name: string } | null;
}

const statusBadge: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
  pending: { variant: 'outline', label: 'Pending' },
  sent: { variant: 'default', label: 'Sent' },
  failed: { variant: 'destructive', label: 'Failed' },
  cancelled: { variant: 'secondary', label: 'Cancelled' },
};

function textToHtml(text: string): string {
  return text
    .split(/\n\n+/)
    .map(p => `<p>${p.replace(/\n/g, '<br/>')}</p>`)
    .join('');
}

const CampaignRecipientsDialog: React.FC<Props> = ({ open, onOpenChange, campaign }) => {
  const qc = useQueryClient();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editEmail, setEditEmail] = useState('');
  const [editName, setEditName] = useState('');
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [verifyFilter, setVerifyFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [regeneratingIds, setRegeneratingIds] = useState<Set<string>>(new Set());
  const [showBatchDelete, setShowBatchDelete] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const generateEmails = useGenerateCampaignEmails();

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['campaign-recipients', campaign.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('email_queue')
        .select('id, recipient_name, recipient_email, subject, status, sent_at, failed_at, html_content, text_content, verification_status, verification_notes, lead_id, leads(name)')
        .eq('campaign_id', campaign.id)
        .order('recipient_name', { ascending: true });
      if (error) throw error;
      return (data || []) as QueueItem[];
    },
    enabled: open,
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, name, email, subject, html_content, text_content }: { id: string; name: string; email: string; subject: string; html_content?: string; text_content?: string }) => {
      const normalized = email.toLowerCase().trim();
      if (!normalized || !normalized.includes('@')) throw new Error('Invalid email');
      if (!subject.trim()) throw new Error('Subject is required');
      const updates: Record<string, string> = { recipient_name: name.trim(), recipient_email: normalized, subject: subject.trim() };
      if (html_content !== undefined) updates.html_content = html_content;
      if (text_content !== undefined) updates.text_content = text_content;
      const { error } = await supabase.from('email_queue').update(updates).eq('id', id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await syncRecipientCount();
      qc.invalidateQueries({ queryKey: ['campaign-recipients', campaign.id] });
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      toast.success('Recipient updated');
      setEditingId(null);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const syncRecipientCount = async () => {
    const { count } = await supabase
      .from('email_queue')
      .select('*', { count: 'exact', head: true })
      .eq('campaign_id', campaign.id);
    await supabase
      .from('email_campaigns')
      .update({ total_recipients: count ?? 0 })
      .eq('id', campaign.id);
  };

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('email_queue').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await syncRecipientCount();
      qc.invalidateQueries({ queryKey: ['campaign-recipients', campaign.id] });
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      toast.success('Recipient removed');
      setDeleteId(null);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const batchDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const { error } = await supabase
        .from('email_queue')
        .delete()
        .in('id', ids)
        .eq('campaign_id', campaign.id);
      if (error) throw error;
    },
    onSuccess: async (_data, ids) => {
      await syncRecipientCount();
      qc.invalidateQueries({ queryKey: ['campaign-recipients', campaign.id] });
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      toast.success(`${ids.length} recipient${ids.length !== 1 ? 's' : ''} removed`);
      setSelectedIds(new Set());
      setShowBatchDelete(false);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const startEdit = (item: QueueItem) => {
    setEditingId(item.id);
    setEditName(item.recipient_name || '');
    setEditEmail(item.recipient_email);
    setEditSubject(item.subject);
    setEditBody(item.html_content || '');
    setExpandedId(item.id);
  };

  const saveEdit = (id: string) => {
    const plainText = stripHtml(editBody);
    updateMutation.mutate({ id, name: editName, email: editEmail, subject: editSubject, html_content: editBody, text_content: plainText });
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const toggleExpand = (id: string) => {
    if (editingId === id) return;
    setExpandedId(prev => (prev === id ? null : id));
  };

  const filteredItems = useMemo(() => {
    return items.filter(i => {
      const q = searchFilter.toLowerCase();
      const matchesSearch = !q || (i.recipient_name || '').toLowerCase().includes(q) || i.recipient_email.toLowerCase().includes(q);
      const matchesVerify = verifyFilter === 'all' ||
        (verifyFilter === 'unverified' && !i.verification_status) ||
        i.verification_status === verifyFilter;
      return matchesSearch && matchesVerify;
    });
  }, [items, searchFilter, verifyFilter]);

  const exportRecipientsToCSV = () => {
    const headers = ['Name', 'Matched Lead', 'Email', 'Subject', 'Status', 'Verification', 'Verification Notes', 'Sent At', 'Email Body'];
    const stripHtmlTags = (html: string) => html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    const escape = (val: string) => {
      if (val.includes(',') || val.includes('"') || val.includes('\n')) {
        return `"${val.replace(/"/g, '""')}"`;
      }
      return val;
    };
    const rows = filteredItems.map(i => [
      i.recipient_name || '',
      (i as any).leads?.name || '',
      i.recipient_email,
      i.subject,
      i.status,
      i.verification_status || '',
      i.verification_notes || '',
      i.sent_at || '',
      i.text_content || (i.html_content ? stripHtmlTags(i.html_content) : ''),
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(escape).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `recipients_${campaign.name.replace(/\s+/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const pending = items.filter(i => i.status === 'pending').length;
  const sent = items.filter(i => i.status === 'sent').length;
  const failed = items.filter(i => i.status === 'failed').length;
  const flagged = items.filter(i => i.verification_status === 'flagged').length;
  const passed = items.filter(i => i.verification_status === 'passed').length;
  const canEdit = ['draft', 'scheduled'].includes(campaign.status);
  const colCount = (canEdit ? 8 : 7) + 1; // +1 for checkbox column

  const selectableFilteredIds = useMemo(() => filteredItems.filter(i => canEdit || i.status === 'pending' || i.status === 'failed').map(i => i.id), [filteredItems, canEdit]);
  const allSelectableSelected = selectableFilteredIds.length > 0 && selectableFilteredIds.every(id => selectedIds.has(id));

  const toggleSelectAll = () => {
    if (allSelectableSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableFilteredIds));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleRegenerateSelected = () => {
    const ids = Array.from(selectedIds);
    const senderConfig = campaign.sender_config as Record<string, any> | null;
    const senderId = senderConfig?.sender_id || campaign.created_by;
    setRegeneratingIds(new Set(ids));
    generateEmails.mutate(
      { campaignId: campaign.id, senderId, queueItemIds: ids },
      {
        onSettled: () => {
          setRegeneratingIds(new Set());
          setSelectedIds(new Set());
          qc.invalidateQueries({ queryKey: ['campaign-recipients', campaign.id] });
        },
      }
    );
  };

  const handleSelectFlagged = () => {
    const flaggedIds = items.filter(i => i.verification_status === 'flagged').map(i => i.id);
    if (flaggedIds.length === 0) return;
    setSelectedIds(new Set(flaggedIds));
  };

  const renderVerificationBadge = (status: string | null) => {
    if (status === 'passed') return <Badge variant="outline" className="gap-1 text-green-700 border-green-300 bg-green-50 dark:text-green-400 dark:border-green-800 dark:bg-green-950"><ShieldCheck className="h-3 w-3" />Passed</Badge>;
    if (status === 'flagged') return <Badge variant="outline" className="gap-1 text-amber-700 border-amber-300 bg-amber-50 dark:text-amber-400 dark:border-amber-800 dark:bg-amber-950"><ShieldAlert className="h-3 w-3" />Flagged</Badge>;
    return <span className="text-xs text-muted-foreground">—</span>;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-7xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Recipients — {campaign.name}
            </DialogTitle>
            <DialogDescription>
              {items.length} total · {pending} pending · {sent} sent · {failed} failed
              {(passed > 0 || flagged > 0) && ` · ${passed} passed · ${flagged} flagged`}
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <div className="flex items-center gap-2 mb-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchFilter}
                  onChange={e => setSearchFilter(e.target.value)}
                  className="pl-9 h-9"
                />
              </div>
              <Select value={verifyFilter} onValueChange={setVerifyFilter}>
                <SelectTrigger className="w-[140px] h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="passed">Passed</SelectItem>
                  <SelectItem value="flagged">Flagged</SelectItem>
                  <SelectItem value="unverified">Unverified</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={exportRecipientsToCSV} disabled={filteredItems.length === 0}>
                <Download className="h-4 w-4 mr-1.5" />
                Export CSV
              </Button>
              {canEdit && (
                <>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".csv,.tsv,.txt"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      try {
                        const text = await file.text();
                        const result = await importRecipientsFromCSV(campaign.id, text, campaign.company_id);
                        qc.invalidateQueries({ queryKey: ['campaign-recipients', campaign.id] });
                        qc.invalidateQueries({ queryKey: ['campaigns'] });
                        let msg = `Parsed ${result.totalParsed} rows: imported ${result.imported}`;
                        if (result.invalidRows > 0) msg += `, ${result.invalidRows} invalid emails skipped`;
                        if (result.duplicates > 0) msg += `, ${result.duplicates} duplicates skipped`;
                        msg += `. ${result.matched} matched to leads, ${result.unmatchedCount} unmatched`;
                        toast.success(msg);
                      } catch (err: any) {
                        toast.error(err.message || 'Import failed');
                      }
                      // Reset input so same file can be re-selected
                      e.target.value = '';
                    }}
                  />
                  <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                    <Upload className="h-4 w-4 mr-1.5" />
                    Import CSV
                  </Button>
                </>
              )}
            </div>
            <div className="sticky top-0 z-10 bg-background">
              {flagged > 0 && canEdit && selectedIds.size === 0 && !generateEmails.isPending && (
                <div className="flex items-center gap-2 mb-3 p-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                  <ShieldAlert className="h-4 w-4 text-amber-600" />
                  <span className="text-sm text-amber-700 dark:text-amber-400">{flagged} flagged email{flagged !== 1 ? 's' : ''}</span>
                  <Button size="sm" variant="default" onClick={handleSelectFlagged} className="ml-auto">
                    <Wand2 className="h-4 w-4 mr-1.5" />
                    Select Flagged
                  </Button>
                </div>
              )}
              {selectedIds.size > 0 && (
                <div className="flex items-center gap-2 mb-3 p-2 rounded-md bg-muted/60 border">
                  <Badge variant="secondary">{selectedIds.size} selected</Badge>
                   <Button size="sm" variant="default" onClick={handleRegenerateSelected} disabled={generateEmails.isPending || batchDeleteMutation.isPending}>
                     <Wand2 className="h-4 w-4 mr-1.5" />
                     {generateEmails.isPending ? 'Regenerating…' : 'Regenerate Selected'}
                   </Button>
                   <Button size="sm" variant="destructive" onClick={() => setShowBatchDelete(true)} disabled={generateEmails.isPending || batchDeleteMutation.isPending}>
                     <Trash2 className="h-4 w-4 mr-1.5" />
                     {batchDeleteMutation.isPending ? 'Deleting…' : 'Delete Selected'}
                   </Button>
                   <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>Clear</Button>
                </div>
              )}
            </div>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10 px-2" onClick={e => e.stopPropagation()}>
                      <Checkbox
                        checked={allSelectableSelected}
                        onCheckedChange={toggleSelectAll}
                        aria-label="Select all pending/failed"
                        disabled={selectableFilteredIds.length === 0}
                      />
                    </TableHead>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Matched Lead</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead className="hidden sm:table-cell">Subject</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Verification</TableHead>
                    {canEdit && <TableHead className="w-[80px]">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={colCount} className="text-center py-8 text-muted-foreground">Loading...</TableCell>
                    </TableRow>
                  ) : items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={colCount} className="text-center py-8 text-muted-foreground">No recipients in queue</TableCell>
                    </TableRow>
                  ) : filteredItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={colCount} className="text-center py-8 text-muted-foreground">No matching recipients</TableCell>
                    </TableRow>
                  ) : filteredItems.map(item => {
                    const badge = statusBadge[item.status] || statusBadge.pending;
                    const isExpanded = expandedId === item.id;
                    const isEditing = editingId === item.id;
                    const isSelectable = canEdit || item.status === 'pending' || item.status === 'failed';

                    return (
                      <React.Fragment key={item.id}>
                        <TableRow className={cn("cursor-pointer hover:bg-muted/50", regeneratingIds.has(item.id) && "animate-pulse bg-green-500/5")} onClick={() => toggleExpand(item.id)}>
                          <TableCell className="px-2" onClick={e => e.stopPropagation()}>
                            {regeneratingIds.has(item.id) ? (
                              <Loader2 className="h-4 w-4 text-green-500 animate-spin" />
                            ) : isSelectable ? (
                              <Checkbox
                                checked={selectedIds.has(item.id)}
                                onCheckedChange={() => toggleSelect(item.id)}
                                aria-label={`Select ${item.recipient_name || item.recipient_email}`}
                              />
                            ) : <span className="block w-4" />}
                          </TableCell>
                          <TableCell className="px-2">
                            {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                          </TableCell>
                          <TableCell className="font-medium">{item.recipient_name || '—'}</TableCell>
                          <TableCell className="text-sm">
                            {item.leads?.name ? (
                              <span className="text-foreground">{item.leads.name}</span>
                            ) : (
                              <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50 dark:text-amber-400 dark:border-amber-800 dark:bg-amber-950">Unmatched</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">{item.recipient_email}</TableCell>
                          <TableCell className="hidden sm:table-cell text-sm text-muted-foreground truncate max-w-[200px]">{item.subject}</TableCell>
                          <TableCell><Badge variant={badge.variant}>{badge.label}</Badge></TableCell>
                          <TableCell>{renderVerificationBadge(item.verification_status)}</TableCell>
                          {canEdit && (
                            <TableCell onClick={e => e.stopPropagation()}>
                              <div className="flex items-center gap-1">
                                {isSelectable && !isEditing && (
                                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => startEdit(item)}>
                                    <Pencil className="h-3.5 w-3.5" />
                                  </Button>
                                )}
                                {isSelectable && (
                                  <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => setDeleteId(item.id)}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          )}
                        </TableRow>

                        {isExpanded && (
                          <TableRow>
                            <TableCell colSpan={colCount} className="p-0 border-b">
                              <div className="p-4 bg-muted/30 space-y-4">
                                {isEditing ? (
                                  <div className="space-y-3">
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                      <div>
                                        <label className="text-xs font-medium text-muted-foreground mb-1 block">Name</label>
                                        <Input value={editName} onChange={e => setEditName(e.target.value)} className="h-9" />
                                      </div>
                                      <div>
                                        <label className="text-xs font-medium text-muted-foreground mb-1 block">Email</label>
                                        <Input value={editEmail} onChange={e => setEditEmail(e.target.value)} className="h-9" />
                                      </div>
                                      <div>
                                        <label className="text-xs font-medium text-muted-foreground mb-1 block">Subject</label>
                                        <Input value={editSubject} onChange={e => setEditSubject(e.target.value)} className="h-9" />
                                      </div>
                                    </div>
                                    <div>
                                      <label className="text-xs font-medium text-muted-foreground mb-1 block">Email Body</label>
                                      <RichTextEditor value={editBody} onChange={setEditBody} />
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Button size="sm" onClick={() => saveEdit(item.id)} disabled={updateMutation.isPending}>
                                        <Save className="h-3.5 w-3.5 mr-1.5" />Save Changes
                                      </Button>
                                      <Button size="sm" variant="outline" onClick={cancelEdit}>Cancel</Button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="space-y-3">
                                    {item.verification_notes && (
                                      <div className={`border rounded-md p-3 text-sm ${item.verification_status === 'flagged' ? 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950' : 'border-green-300 bg-green-50 dark:border-green-800 dark:bg-green-950'}`}>
                                        <div className="flex items-center gap-1.5 mb-1 font-medium text-xs text-slate-900">
                                          {item.verification_status === 'flagged' ? <ShieldAlert className="h-3.5 w-3.5 text-amber-600" /> : <ShieldCheck className="h-3.5 w-3.5 text-green-600" />}
                                          Verification Notes
                                        </div>
                                        <p className={item.verification_status === 'flagged' ? 'text-amber-900 dark:text-amber-200' : 'text-green-900 dark:text-green-200'}>{item.verification_notes}</p>
                                      </div>
                                    )}
                                    <div>
                                      <div className="text-xs font-medium text-muted-foreground mb-2">Email Preview</div>
                                      <div
                                        className="border rounded-md bg-white text-gray-900 p-4 max-h-[400px] overflow-auto text-sm prose prose-sm max-w-none"
                                        dangerouslySetInnerHTML={{ __html: item.html_content || '<p class="text-muted-foreground">No content</p>' }}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Recipient?</AlertDialogTitle>
            <AlertDialogDescription>This will remove the recipient from the campaign queue.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (deleteId) deleteMutation.mutate(deleteId); }}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showBatchDelete} onOpenChange={setShowBatchDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} recipient{selectedIds.size !== 1 ? 's' : ''}?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently remove the selected recipients from this campaign's queue. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => batchDeleteMutation.mutate(Array.from(selectedIds))}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default CampaignRecipientsDialog;
